import {applySnapshot, cast, flow, Instance, types} from 'mobx-state-tree';
import {ELogicalElement, i18n} from '@progress-fe/core';
import {ISvgMenuItem} from '@progress-fe/ui-kit';
import {XYPosition} from '@xyflow/react';
import {v4 as uuidv4} from 'uuid';
import {ELEMENTS_LIST} from 'core/mocks/projects/projects.mocks';
import {
  ResetModel,
  RequestModel,
  JsonSchemaForm,
  LogicalElementInfo,
  TLogicalElementInfoModel
} from 'core/models';
import {
  RJSFSchemas,
  TechProcessApi,
  LogicalUpdateOut,
  LogicalElementsOut,
  CreateLogicalElementActionResult
} from 'api';

import {IEntityToRefresh} from '../../../../core/interfaces';

const ProjectLogicalElements = types
  .compose(
    ResetModel,
    types.model('ProjectLogicalElements', {
      projectUuid: '',
      checkpointUuid: '',

      logicalElements: types.optional(types.array(LogicalElementInfo), []),
      jsonForm: types.maybeNull(JsonSchemaForm),

      fetchRequest: types.optional(RequestModel, {}),
      createRequest: types.optional(RequestModel, {}),
      duplicateRequest: types.optional(RequestModel, {}),
      deleteRequest: types.optional(RequestModel, {})
    })
  )
  .actions((self) => ({
    hasLogicalElement(uuid: string) {
      return self.logicalElements.some((m) => m.uuid === uuid);
    },
    findLogicalElement(uuid: string): TLogicalElementInfoModel | undefined {
      return self.logicalElements.find((e) => e.uuid === uuid);
    }
  }))
  .actions((self) => ({
    _clearJsonForm() {
      self.jsonForm?.clear();
      self.jsonForm = null;
    },
    _reloadJsonForm: flow(function* (uuid: string) {
      self.jsonForm = JsonSchemaForm.create({entityUuid: uuid});
      const response: {[key: string]: RJSFSchemas} = yield self.jsonForm.fetchRequest.send(
        TechProcessApi.techProcessGetLogicalElementSchemas.bind(TechProcessApi),
        {
          logicalElementUuid: uuid,
          projectUuid: self.projectUuid,
          checkpointUuid: self.checkpointUuid
        }
      );

      if (response) {
        self.jsonForm = JsonSchemaForm.create({entityUuid: uuid});
        self.jsonForm.setJsonSchemas(response);
      }
    })
  }))
  .actions((self) => ({
    add: flow(function* (type: ELogicalElement, position: XYPosition) {
      const response: CreateLogicalElementActionResult = yield self.createRequest.send(
        TechProcessApi.techProcessCreateLogicalElement.bind(TechProcessApi),
        {
          projectUuid: self.projectUuid,
          checkpointUuid: self.checkpointUuid,
          idempotencyKey: uuidv4(),
          logicalElementIn: {
            type: type,
            position: position
          }
        }
      );

      return self.createRequest.isDone && !!response
        ? LogicalElementInfo.create({
            uuid: response.data.uuid,
            name: response.data.name,
            lastUpdated: new Date(),
            deletable: response.data.deletable,
            logicalType: response.data.type as ELogicalElement
          })
        : null;
    }),
    duplicate: flow(function* (uuid: string) {
      const response: CreateLogicalElementActionResult = yield self.duplicateRequest.send(
        TechProcessApi.techProcessDuplicateLogicalElement.bind(TechProcessApi),
        {
          projectUuid: self.projectUuid,
          checkpointUuid: self.checkpointUuid,
          idempotencyKey: uuidv4(),
          elementUuid: uuid
        }
      );

      return response?.data.uuid;
    }),
    remove: flow(function* (uuids: Array<string>) {
      yield self.deleteRequest.send(
        TechProcessApi.techProcessDeleteLogicalElementGroup.bind(TechProcessApi),
        {
          projectUuid: self.projectUuid,
          checkpointUuid: self.checkpointUuid,
          logicalElementsToDeleteIn: {
            logicalElements: uuids
          }
        }
      );
      return self.deleteRequest.isDone;
    })
  }))
  .actions((self) => ({
    _updateLogicalElementName(uuid: string, name: string) {
      const element = self.logicalElements.find((n) => n.uuid === uuid);
      element?.setName(name);
    }
  }))
  .actions((self) => ({
    _updateLogicalElementFormData: flow(function* (uuid: string, schemaId: string, data: unknown) {
      const jsonSchema = self.jsonForm?.jsonSchemas.find((js) => js.id === schemaId);
      if (!self.jsonForm || !jsonSchema) {
        return null;
      }

      jsonSchema.updateFormData(data);
      const response: LogicalUpdateOut = yield self.jsonForm.updateRequest.send(
        TechProcessApi.techProcessUpdateLogicalElementInstance.bind(TechProcessApi),
        {
          logicalElementUuid: uuid,
          body: data as object,
          projectUuid: self.projectUuid,
          checkpointUuid: self.checkpointUuid
        }
      );

      if (!response) {
        return null;
      }
      if (response.name) {
        self._updateLogicalElementName(uuid, response.name);
      }
      if (response.schemas) {
        self.jsonForm?.setJsonSchemas(response.schemas);
      }

      const entityToRefresh: IEntityToRefresh = {
        dataChanged: false,
        modelsChanged: false,
        elementsChanged: false,
        logicalElementsChanged: false,
        treeChanged: response.treeChanged
      };

      return {
        workzoneChanged: response.workzoneChanged || false,
        changedEntities: entityToRefresh
      };
    })
  }))
  .actions((self) => ({
    _loadLogicalElements: flow(function* () {
      const mocks = ELEMENTS_LIST.find((m) => m.projectId === self.projectUuid)?.logicalItems || [];
      if (mocks.length) {
        self.logicalElements = cast(mocks);
        return;
      }

      const response: LogicalElementsOut = yield self.fetchRequest.send(
        TechProcessApi.techProcessGetLogicalElements.bind(TechProcessApi),
        {
          projectUuid: self.projectUuid,
          checkpointUuid: self.checkpointUuid
        }
      );

      const responseLogicalElements = response.logicalElements.map((el) => ({
        uuid: el.uuid,
        name: el.name,
        lastUpdated: new Date(),
        deletable: el.deletable,
        logicalType: el.type as ELogicalElement
      }));

      applySnapshot(self.logicalElements, responseLogicalElements);
    })
  }))
  .actions((self) => ({
    _reload: flow(function* () {
      yield self._loadLogicalElements();
    })
  }))
  .actions((self) => ({
    init: flow(function* (projectUuid: string, checkpointUuid: string) {
      self.resetModel();
      self.projectUuid = projectUuid;
      self.checkpointUuid = checkpointUuid;
      yield self._reload();
    })
  }))
  .views((self) => ({
    get isLoading(): boolean {
      return (
        self.fetchRequest.isPending ||
        self.createRequest.isPending ||
        self.duplicateRequest.isPending ||
        self.deleteRequest.isPending
      );
    },
    get isFormLoading(): boolean {
      return self.jsonForm?.isFormLoading ?? false;
    },
    get isFormDataUpdating(): boolean {
      return self.jsonForm?.isFormDataUpdating ?? false;
    },
    get logicalMenuItems(): ISvgMenuItem<ELogicalElement>[] {
      return [
        {
          id: ELogicalElement.Recycle,
          name: i18n.t(`enum.logicalElement.${ELogicalElement.Recycle}`)
        },
        {
          id: ELogicalElement.Function,
          name: i18n.t(`enum.logicalElement.${ELogicalElement.Function}`)
        },
        {
          id: ELogicalElement.Optimization,
          name: i18n.t(`enum.logicalElement.${ELogicalElement.Optimization}`)
        },
        {
          id: ELogicalElement.Set,
          name: i18n.t(`enum.logicalElement.${ELogicalElement.Set}`),
          isDisabled: true
        },
        {
          id: ELogicalElement.Cutter,
          name: i18n.t(`enum.logicalElement.${ELogicalElement.Cutter}`),
          isDisabled: true
        }
      ];
    }
  }));

export type TProjectLogicalElementsModel = Instance<typeof ProjectLogicalElements>;

export {ProjectLogicalElements};
